// VariantBar: tabbladen voor de opties van een cluster (bijv. mogelijke
// opstelplekken). Per optie: naam, voorkeur markeren; laatste optie verwijderen.

import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export default function VariantBar({ state, current, onSelect, onUpdate }) {
    const [name, setName] = useState(state.names?.[current] || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => { setName(state.names?.[current] || ''); }, [current, state.names]);

    const run = async (body, after) => {
        setBusy(true);
        setError(null);
        try {
            await onUpdate(body);
            after?.();
        } catch (err) {
            setError(err?.statusCode ? (err.message || 'Wijzigen mislukt') : 'Geen verbinding met de server. Probeer het opnieuw.');
        } finally {
            setBusy(false);
        }
    };

    const saveName = () => {
        const trimmed = name.trim();
        if (trimmed === (state.names?.[current] || '')) { return; }
        run({ names: { [current]: trimmed } });
    };

    const addOption = () => run({ count: state.count + 1 }, () => onSelect(state.count + 1));

    const removeLast = () => {
        const label = `${state.label} ${state.count}${state.names?.[state.count] ? ` (${state.names[state.count]})` : ''}`;
        if (!window.confirm(`${label} verwijderen? Alle antwoorden en foto's van deze optie gaan verloren.`)) { return; }
        run({ count: state.count - 1 }, () => { if (current >= state.count) { onSelect(state.count - 1); } });
    };

    const isPreferred = current === state.preferred;
    const isLast = current === state.count;

    return (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Leg elke mogelijke positie vast als aparte optie. De optie met de ster is de voorkeur:
                die telt voor de voortgang, het risicoregister en het werkplan. Het opnamerapport toont alle opties.
            </Typography>
            <Tabs
                value={current}
                onChange={(_, v) => onSelect(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ mb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
            >
                {Array.from({ length: state.count }, (_, i) => i + 1).map(v => (
                    <Tab
                        key={v}
                        value={v}
                        icon={v === state.preferred ? <StarIcon fontSize="small" color="warning" /> : undefined}
                        iconPosition="start"
                        label={state.names?.[v] ? `${v}. ${state.names[v]}` : `Optie ${v}`}
                        sx={{ textTransform: 'none', minHeight: 44, maxWidth: 260 }}
                    />
                ))}
            </Tabs>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField
                    label={`Naam van ${state.label.toLowerCase()} ${current}`}
                    placeholder="Bijv. parkeerplaats achterzijde"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={saveName}
                    size="small"
                    inputProps={{ maxLength: 60 }}
                    sx={{ flex: '1 1 240px', maxWidth: 420 }}
                />
                <Button
                    variant={isPreferred ? 'contained' : 'outlined'}
                    color="warning"
                    startIcon={isPreferred ? <StarIcon /> : <StarBorderIcon />}
                    onClick={() => !isPreferred && run({ preferred: current })}
                    disabled={busy}
                >
                    {isPreferred ? 'Voorkeursoptie' : 'Als voorkeur kiezen'}
                </Button>
                {state.count < state.max && (
                    <Button startIcon={<AddIcon />} onClick={addOption} disabled={busy}>
                        Optie toevoegen
                    </Button>
                )}
                {state.count > 1 && isLast && (
                    <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={removeLast} disabled={busy}>
                        Deze optie verwijderen
                    </Button>
                )}
            </Stack>
            {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        </Box>
    );
}
